import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import {
  AppointmentDto,
  EventTimeSlotDto,
  TimeSlotDto,
} from "../../../api-client";
import { calendarKeys } from "../../../stores/Calendar/calendar.keys";
import { appointmentKeys } from "../../../stores/Appointment/appointment.keys";
import { AppointmentWebSocketEvent } from "../../../stores/WebSocket/types";
import useStore from "../../../helpers/useStore";
import { WebSocketContext } from "../../../stores/WebSocket/WebSocket.provider";
import {
  applyCalendarCachePatch,
  updateTimeslotDocumentation,
  isStaleSelfDeleteEcho,
} from "../../../stores/Calendar/AppointmentsOfAllTypes.utils";
import notificationStore from "../../../stores/Notification/notificationStore";
import { CLIENT_ID } from "../../../api/clientId";

/**
 * Incoming calendar socket events (from OTHER users — self-events are suppressed) are
 * buffered and flushed on two AND-ed conditions, so we never re-render the calendar
 * mid-action yet still stay reasonably fresh:
 *
 *  1. THROTTLE (min interval): don't flush more often than this. Caps render frequency
 *     when someone else is busy while this user is idle.
 *  2. IDLE-GATE (debounce on THIS user's activity): don't flush while this user is
 *     actively working. "Activity" = any mutation lifecycle event; we wait until they've
 *     been quiet for the idle window before flushing.
 *
 * A flush happens only once BOTH are satisfied. There is deliberately no max-wait cap:
 * the user acts in milliseconds while freshness is only needed on a ~20s scale, and
 * avoiding the jerky mid-action interruption matters more than bounded staleness. So
 * while the user works nonstop we keep deferring; the moment they pause we catch up.
 */
const SOCKET_MIN_FLUSH_INTERVAL_MS = 20000;
/**
 * How long this user's mutations must be quiet before a flush is allowed. Must be wide
 * enough to bridge the natural gaps between discrete actions (drag, think/hover, drag),
 * so a brief pause mid-planning doesn't trigger an interrupting flush. Tunable.
 */
const SOCKET_IDLE_QUIET_MS = 5000;

/** A buffered event plus the moment it arrived, which bounds staleness checks. */
interface BufferedSocketEvent {
  event: AppointmentWebSocketEvent;
  receivedAt: number;
}

/**
 * The therapist user id(s) a socket event refers to, read from its timeslots'
 * `user`. Appointments carry the therapist on each timeslot; standalone
 * timeslots (EventTimeSlot / LeaveOfAbsence / TravelTime) carry it on the item
 * itself. The single-therapist (date-range) cache is keyed by therapist userId,
 * so this is what decides whether an event is relevant to a given single-user
 * view.
 */
const eventTimeslotUserIds = (event: AppointmentWebSocketEvent): Set<string> => {
  const ids = new Set<string>();
  const data = event.data as any;
  if (!data) return ids;
  for (const ts of data.timeSlots ?? []) {
    const id = ts?.user?.id;
    if (id) ids.add(id);
  }
  if (data.user?.id) ids.add(data.user.id);
  return ids;
};

/**
 * Custom hook to handle WebSocket events for calendar updates
 * This hook should be used in components that need real-time calendar updates
 */
export const useSocketHandler = () => {
  const queryClient = useQueryClient();
  const WebSocketStore = useStore(WebSocketContext);

  // Tracks whether this handler was connected before, to distinguish the
  // initial connection from a reconnect.
  const wasConnectedRef = useRef(false);

  // Buffered socket events awaiting the next flush, plus scheduling bookkeeping.
  // Persisted across renders so the effect can keep appending without rearming.
  // Buffered with the moment they arrived: a self-originated Delete is only
  // applied if this tab has not put the appointment back since (see
  // `wasAppointmentAffirmedSince`).
  const pendingEventsRef = useRef<BufferedSocketEvent[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // When the buffer was last drained — drives the THROTTLE gate.
  const lastFlushAtRef = useRef<number>(0);
  // When this user was last active (any mutation lifecycle event) — drives the IDLE gate.
  const lastActivityAtRef = useRef<number>(0);

  useEffect(() => {
    // console.debug("SocketHandler: useEffect triggered, checking WebSocketStore", WebSocketStore);
    // Only set up listeners when we have a connected WebSocket
    if (!WebSocketStore || !WebSocketStore.isConnected) {
      return;
    }

    // Reconnect recovery: events broadcast while we were disconnected are lost,
    // so refetch the calendar data to resynchronize.
    if (wasConnectedRef.current) {
      queryClient.invalidateQueries({
        queryKey: calendarKeys.appointmentsForDate.all(),
      });
      // Single-therapist view reads its own date-range cache (the patches below
      // only touch the per-day cache). Inactive in the main view, so refetching
      // it here is a no-op there.
      queryClient.invalidateQueries({
        queryKey: calendarKeys.appointmentsForDateRange.all(),
      });
    }
    wasConnectedRef.current = true;

    // console.debug("SocketHandler: WebSocket is connected, setting up event listeners");

    // Resync the single-therapist (date-range) cache, but ONLY for the therapist
    // the event actually touches. That cache is keyed by userId, so a user working
    // in single-therapist mode is refetched only when an incoming event affects
    // THEIR therapist — the constant socket traffic for other therapists never
    // disturbs their view. Gated by the flush (throttle + idle) like everything
    // else here, and a no-op when no single-user cache matches (e.g. main view).
    const invalidateMatchingRangeCaches = (event: AppointmentWebSocketEvent) => {
      const userIds = eventTimeslotUserIds(event);
      if (userIds.size === 0) return;
      queryClient.invalidateQueries({
        queryKey: calendarKeys.appointmentsForDateRange.all(),
        predicate: (query) => {
          const params = (query.queryKey as readonly unknown[])[1] as
            | { userId?: string }
            | undefined;
          return !!params?.userId && userIds.has(params.userId);
        },
      });
    };

    // Apply a single already-filtered socket event to the caches. Pure cache work
    // only — echo suppression happens at enqueue time so self-events never buffer.
    const processEvent = ({ event, receivedAt }: BufferedSocketEvent) => {
        const { type, action, data: appointment, source } = event;

        // A self-originated Delete that this tab has since overruled by putting
        // the appointment back on the calendar is stale — applying it would take
        // away what the user just planned. Only echoes are filtered this way;
        // another user's deletion is authoritative and always applies.
        if (isStaleSelfDeleteEcho(event, receivedAt, CLIENT_ID)) {
          // console.debug("🔌🔇 SocketHandler: dropping stale self Delete", appointment?.id);
          return;
        }

        // console.log(`🔌⚡ SocketHandler: ${action} ${type} from ${source}`, appointment);

        // type is the BROADCAST type, and can be one of:
        /*   TimeSlot = "TimeSlot",
              Appointment = "Appointment",
              ContinuousTimeSlot = "ContinuousTimeSlot",
              ContinuousAppointment = "ContinuousAppointment",
              EventTimeSlot = "EventTimeSlot",
              ContinuousEventTimeSlot = "ContinuousEventTimeSlot",
              SimpleWorkingHours = "SimpleWorkingHours",
              CancelledAppointment = "CancelledAppointment",
            */
        if (type === "Appointment") {          
          const dto = appointment as AppointmentDto;
          // console.debug("SocketHandler: Appointment event received:", dto, action);
          applyCalendarCachePatch(queryClient, "appointments", action, dto);
          invalidateMatchingRangeCaches(event);
        } else if (type === "TimeSlot") {
          const dto = appointment as TimeSlotDto;
          const tsType = dto.type;

          switch (tsType) {
            case "EventTimeSlot": {
              applyCalendarCachePatch(queryClient, "eventTimeSlots", action, appointment as EventTimeSlotDto);
              invalidateMatchingRangeCaches(event);
              break;
            }

            case "LeaveOfAbsence": {
              applyCalendarCachePatch(queryClient, "leaveOfAbsences", action, appointment as TimeSlotDto);
              invalidateMatchingRangeCaches(event);
              break;
            }

            case "TravelTime": {
              applyCalendarCachePatch(queryClient, "travelTimes", action, appointment as TimeSlotDto);
              invalidateMatchingRangeCaches(event);
              break;
            }

            case "SimpleWorkingHours": {
              // Handle SimpleWorkingHours if needed
              // console.log("🔌⏰ SocketHandler: SimpleWorkingHours event received:", appointment);
              break;
            }

            default: {
              // Show warning for unknown event types
              const message = `🔌❌ SocketHandler: Unknown WebSocket event type: ${type}`;
              console.warn(message, event);
              notificationStore.showMessage(message, "warning");
              break;
            }
          }
        } else {
          // NON TIMESLOTS/APPOINTMENTS
          switch (type) {
            // Continuous (template) objects live in separate query caches with a
            // different structure than AppointmentsOfAllTypesDto, so we cannot
            // patch them with `apply`. Targeted invalidation keeps other users'
            // Dauertermin views in sync (self-originated events are filtered
            // above; the originator's own mutations invalidate these caches).
            case "ContinuousTimeSlot":
            case "ContinuousEventTimeSlot":
            case "ContinuousAppointment": {
              queryClient.invalidateQueries({
                queryKey: calendarKeys.continuousAppointmentsByDay.all(),
              });
              queryClient.invalidateQueries({
                queryKey: calendarKeys.continuousAppointmentsForUser.all(),
              });
              queryClient.invalidateQueries({
                queryKey: calendarKeys.continuousAppointmentsForPatient.all(),
              });
              break;
            }

            case "CancelledAppointment": {
              const dto = appointment as AppointmentDto;

              // A cancellation the PRACTICE made arrives as a Delete with
              // cancelledAppData: mutate the cancellation fields in-place and
              // Update, so the event flips to its cancelled rendering instead
              // of vanishing — the slot is a hole someone has to refill.
              //
              // MD-510: a PATIENT cancellation is not ours to chase, so it
              // falls through to the plain Delete below and leaves the
              // calendar, exactly as it did before 0.4.0. This mirrors the
              // server's own `byTherapist = true` filter on both cancelled
              // queries; widening it here without widening it there would only
              // make the live view disagree with the next refetch.
              if (action === "Delete" && appointment.cancelledAppData?.byTherapist) {
                const patched: AppointmentDto = {
                  ...dto,
                  end: undefined,
                  originalDate: appointment.cancelledAppData.originalDate,
                  resolved: appointment.cancelledAppData.resolved,
                  reason: appointment.cancelledAppData.reason,
                  byTherapist: appointment.cancelledAppData.byTherapist,
                };
                applyCalendarCachePatch(queryClient, "appointments", "Update", patched);
                invalidateMatchingRangeCaches(event);
                break;
              }

              // Handle other cancelled appointment cases
              applyCalendarCachePatch(queryClient, "appointments", action, dto);
              invalidateMatchingRangeCaches(event);
              break;
            }

            case "TreatmentNote": {
              const treatmentId = appointment?.treatmentId;
              const patientId = appointment?.patientId;
              
              if (!treatmentId) {
                console.error("🔌❌ TreatmentNote event missing treatmentId:", appointment);
                notificationStore.showMessage("TreatmentNote sync failed: missing treatment ID", "error");
                break;
              }

              // Use updateTimeslotDocumentation updater on the same caches as apply
              const updater = updateTimeslotDocumentation(treatmentId, action, appointment);
              queryClient.setQueriesData(
                { queryKey: calendarKeys.appointmentsForDate.all() },
                updater
              );

              // Also invalidate patient-specific appointment queries to keep them in sync
              if (patientId) {
                queryClient.invalidateQueries({
                  queryKey: [appointmentKeys.filteredAppointmentsByPatient.scope],
                  predicate: (query) =>
                    query.queryKey.some(
                      (k: any) => typeof k === "object" && k?.patientId === patientId,
                    ),
                });
                queryClient.invalidateQueries({
                  queryKey: [appointmentKeys.appointmentsByPatient.scope],
                  predicate: (query) =>
                    query.queryKey.some(
                      (k: any) => typeof k === "object" && k?.patientId === patientId,
                    ),
                });
              }
              break;
            }
            default: {
              // Show warning for unknown event types
              const message = `🔌❌ SocketHandler: Unknown WebSocket event type: ${type}`;
              console.warn(message, event);
              notificationStore.showMessage(message, "warning");
              break;
            }
          } // end of switch(type) for non timeslot/appointment
        } // end of else for non timeslot/appointment
    };

    // Drain the buffer in arrival order. All cache writes happen synchronously here,
    // so React Query coalesces them into a single subscriber notification → one
    // calendar re-render per flush, regardless of how many events arrived.
    const flushNow = () => {
      const events = pendingEventsRef.current;
      if (events.length === 0) return;
      pendingEventsRef.current = [];
      lastFlushAtRef.current = Date.now();
      events.forEach(processEvent);
    };

    // Re-evaluate the two gates and either flush or reschedule for the earliest time
    // both could be satisfied. Self-rescheduling so that ongoing user activity keeps
    // pushing the idle gate back without ever starving (other users can't reset it).
    const evaluateFlush = () => {
      flushTimerRef.current = null;
      if (pendingEventsRef.current.length === 0) return;

      const now = Date.now();
      const sinceFlush = now - lastFlushAtRef.current;
      const sinceActivity = now - lastActivityAtRef.current;
      const waitForInterval = SOCKET_MIN_FLUSH_INTERVAL_MS - sinceFlush; // THROTTLE
      const waitForIdle = SOCKET_IDLE_QUIET_MS - sinceActivity; // IDLE-GATE

      if (waitForInterval <= 0 && waitForIdle <= 0) {
        flushNow();
        return;
      }

      // Not ready yet — wake up when the later of the two gates is due, then re-check
      // (activity that arrives in the meantime will have pushed waitForIdle out again).
      const delay = Math.max(waitForInterval, waitForIdle, 50);
      flushTimerRef.current = setTimeout(evaluateFlush, delay);
    };

    // Ensure a re-evaluation is scheduled without resetting an already-pending one
    // (new events don't change either gate's timing, so the existing wake-up stands).
    const scheduleFlush = () => {
      if (flushTimerRef.current === null) {
        evaluateFlush();
      }
    };

    // Buffer an event, then schedule a gated flush. We never flush inline: even if the
    // throttle is satisfied, the idle gate must also be clear (this user not mid-action).
    const enqueue = (event: AppointmentWebSocketEvent) => {
      // Echo suppression: ignore broadcasts for changes this tab initiated.
      // The originator's cache is already correct via the mutation's
      // onMutate/onSuccess handlers — re-applying the echo would only cause
      // a redundant rebuild and could overwrite newer optimistic state during
      // rapid successive actions.
      // EXCEPTION: self-originated Delete echoes ARE processed. Slot-level
      // SPLIT/MERGE side effects delete appointments that the HTTP response
      // does not enumerate; the delete patch is cheap and idempotent.
      if (event.clientId && event.clientId === CLIENT_ID && event.action !== "Delete") {
        // console.debug(`🔌🔇 SocketHandler: ignoring self-originated ${event.action} ${event.type}`);
        return;
      }

      pendingEventsRef.current.push({ event, receivedAt: Date.now() });
      scheduleFlush();
    };

    // Track THIS user's activity to drive the idle gate. Any mutation lifecycle event
    // (start, success/error, removal) stamps the activity clock, so the idle window is
    // measured from the moment the user's last action settled. Subscribing to the
    // mutation cache (rather than the useIsMutating hook) keeps this re-render free and
    // contained in this effect — no socket-listener churn, no cost to CalendarController.
    const unsubscribeActivity = queryClient.getMutationCache().subscribe(() => {
      lastActivityAtRef.current = Date.now();
    });

    // Type-safe appointment event handler
    const unsubscribeAppointments = WebSocketStore.onAppointmentEvent(enqueue);

    return () => {
      unsubscribeAppointments();
      unsubscribeActivity();
      // Flush anything still buffered so a benign effect re-run (e.g. queryClient
      // identity change) doesn't drop pending updates, then clear the timer.
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      flushNow();
    };
  }, [WebSocketStore?.isConnected, queryClient]); // Only depend on connection state, not the entire store
};

// Create an observer wrapper component that can be used when the hook needs to observe MobX state
export const SocketHandlerObserver = observer(() => {
  useSocketHandler();
  return null; // This component doesn't render anything
});
